import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { SOCIAL } from "@/lib/config";
import Icon from "@/components/shared/Icon";
import Seo from "@/components/shared/Seo";
import PageHero from "@/components/shared/PageHero";

export default function Community() {
  const { t, lp, code } = useLang();

  const challenges = [
    { icon: "BookOpen", en: "Read 3 stories this week", zh: "本周读 3 个故事" },
    { icon: "Calculator", en: "Finish a Mathematics lesson", zh: "完成一节数学课" },
    { icon: "Gamepad2", en: "Beat your best game score", zh: "刷新你的游戏最佳成绩" },
    { icon: "Heart", en: "Do one kind act and tell a grown-up", zh: "做一件善事并告诉大人" },
  ];
  const milestones = [
    { en: "Complete your first lesson", zh: "完成你的第一节课" },
    { en: "Explore all seven subjects", zh: "探索全部七个主题" },
    { en: "Read every starter story", zh: "读完所有入门故事" },
    { en: "Try all six games", zh: "尝试全部六款游戏" },
  ];

  return (
    <>
      <Seo title={t("community.title")} description={t("community.missionDesc")} />
      <PageHero eyebrow={t("nav.community")} title={t("community.title")} subtitle={t("community.missionDesc")} />

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border-2 border-[#147DEB]/10 bg-white p-7">
            <Icon name="Trophy" className="h-8 w-8 text-[#FFC928]" />
            <h2 className="font-display mt-4 text-2xl font-bold text-[#082B66]">{t("community.challenges")}</h2>
            <ul className="mt-4 space-y-3">
              {challenges.map((c, i) => (
                <li key={i} className="flex items-center gap-3 text-base text-[#334155]" data-testid={`community-challenge-${i}`}>
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#E8F5FF]"><Icon name={c.icon} className="h-4.5 w-4.5 text-[#147DEB]" /></span>
                  {code === "zh" ? c.zh : c.en}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border-2 border-[#45C878]/20 bg-gradient-to-br from-[#EAFBF1] to-white p-7">
            <Icon name="Flag" className="h-8 w-8 text-[#45C878]" />
            <h2 className="font-display mt-4 text-2xl font-bold text-[#082B66]">{t("community.milestones")}</h2>
            <ul className="mt-4 space-y-3">
              {milestones.map((m, i) => (
                <li key={i} className="flex items-center gap-3 text-base text-[#334155]">
                  <Icon name="CheckCircle2" className="h-5 w-5 flex-shrink-0 text-[#45C878]" />
                  {code === "zh" ? m.zh : m.en}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-6 rounded-3xl border-2 border-[#147DEB]/10 bg-white p-7">
          <Icon name="HandHeart" className="h-8 w-8 text-[#FF91B8]" />
          <h2 className="font-display mt-4 text-2xl font-bold text-[#082B66]">{t("community.contributions")}</h2>
          <p className="mt-2 text-base leading-relaxed text-[#475569]">{t("community.missionDesc")}</p>
          <Link to={lp("/impact")} className="mt-4 inline-flex items-center gap-1.5 rounded-2xl bg-[#E8F5FF] px-5 py-2.5 text-sm font-bold text-[#147DEB] hover:bg-[#D4EDFF]" data-testid="community-records-link">
            <Icon name="ShieldCheck" className="h-4 w-4" /> {t("community.records")} <Icon name="ArrowRight" className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-6 rounded-3xl border-2 border-[#147DEB]/10 bg-white p-7">
          <h2 className="font-display text-2xl font-bold text-[#082B66]">{t("community.guidelines")}</h2>
          <p className="mt-2 text-base leading-relaxed text-[#475569]">{t("community.guidelinesDesc")}</p>
          <p className="mt-4 inline-flex items-start gap-2 rounded-2xl bg-[#F8FBFF] p-3 text-sm font-semibold text-[#64748B]">
            <Icon name="Info" className="mt-0.5 h-4 w-4 flex-shrink-0" /> {t("community.noFake")}
          </p>
        </div>

        {/* X only */}
        <div className="mt-6 flex items-center justify-between rounded-3xl bg-[#082B66] p-7 text-white">
          <div>
            <h3 className="font-display text-xl font-bold">{t("footer.follow")}</h3>
            <p className="mt-1 text-sm text-white/70">X {code === "zh" ? "是唯一的官方社交渠道" : "is our only social channel"}</p>
          </div>
          {SOCIAL.xUrl ? (
            <a href={SOCIAL.xUrl} target="_blank" rel="noopener noreferrer" data-testid="community-x-link" className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 font-bold text-[#082B66]">
              <Icon name="Twitter" className="h-5 w-5" /> X
            </a>
          ) : (
            <span data-testid="community-x-placeholder" className="inline-flex items-center gap-2 rounded-2xl bg-white/10 px-5 py-3 font-bold text-white/70">
              <Icon name="Twitter" className="h-5 w-5" /> {t("common.comingSoon")}
            </span>
          )}
        </div>
      </section>
    </>
  );
}
